/**
 * M1 (jun-2026) — Marketplace: Vendedor Verificado.
 * Cliente de los endpoints vivos de /marketplace y /admin/marketplace.
 */
import { api } from '@/lib/api';
import { tr } from '@/contexts/LanguageContext';

export const marketplaceApi = {
  categories() { return api.get('/marketplace/categories'); },
  sellerEligibility() { return api.get('/marketplace/seller/eligibility'); },
  sellerMe() { return api.get('/marketplace/seller/me'); },
  sellerApply(body) { return api.post('/marketplace/seller/apply', body); },
  sellerSubscriptionCheckout() {
    return api.post('/marketplace/seller/subscription/checkout', {});
  },
  // M2 — productos del vendedor (Marca verificada)
  myProducts(status) {
    const q = status ? `?status=${status}` : '';
    return api.get(`/marketplace/seller/products${q}`);
  },
  createProduct(body) { return api.post('/marketplace/seller/products', body); },
  updateProduct(id, body) { return api.patch(`/marketplace/seller/products/${id}`, body); },
  pauseProduct(id) { return api.post(`/marketplace/seller/products/${id}/pause`, {}); },
  resumeProduct(id) { return api.post(`/marketplace/seller/products/${id}/resume`, {}); },
  archiveProduct(id) { return api.delete(`/marketplace/seller/products/${id}`); },
  // M3 — cursos del vendedor (barbero top N)
  myCourses() { return api.get('/marketplace/seller/courses'); },
  createCourse(body) { return api.post('/marketplace/seller/courses', body); },
  updateCourse(id, body) { return api.patch(`/marketplace/seller/courses/${id}`, body); },
  submitCourse(id) { return api.post(`/marketplace/seller/courses/${id}/submit`, {}); },
  pauseCourse(id) { return api.post(`/marketplace/seller/courses/${id}/pause`, {}); },
  resumeCourse(id) { return api.post(`/marketplace/seller/courses/${id}/resume`, {}); },
  archiveCourse(id) { return api.delete(`/marketplace/seller/courses/${id}`); },
  courseLessons(id) { return api.get(`/marketplace/seller/courses/${id}/lessons`); },
  addModule(id, body) { return api.post(`/marketplace/seller/courses/${id}/modules`, body); },
  updateModule(id, mid, body) { return api.patch(`/marketplace/seller/courses/${id}/modules/${mid}`, body); },
  deleteModule(id, mid) { return api.delete(`/marketplace/seller/courses/${id}/modules/${mid}`); },
  addLesson(id, body) { return api.post(`/marketplace/seller/courses/${id}/lessons`, body); },
  updateLesson(id, lid, body) { return api.patch(`/marketplace/seller/courses/${id}/lessons/${lid}`, body); },
  deleteLesson(id, lid) { return api.delete(`/marketplace/seller/courses/${id}/lessons/${lid}`); },
  // M3 — alumno: enroll gratis real, player y progreso
  enrollCourse(id) { return api.post(`/marketplace/courses/${id}/enroll`, {}); },
  coursePlayer(id) { return api.get(`/marketplace/courses/${id}/player`); },
  saveLessonProgress(id, lid, body) {
    return api.post(`/marketplace/courses/${id}/lessons/${lid}/progress`, body);
  },
  // M4 — vitrina pública (solo contenido activo)
  catalogCounts() { return api.get('/marketplace/catalog/counts'); },
  catalogProducts(params = {}) {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null)).toString();
    return api.get(`/marketplace/catalog/products${q ? `?${q}` : ''}`);
  },
  catalogProduct(id) { return api.get(`/marketplace/catalog/products/${id}`); },
  productPurchaseIntent(id) {
    return api.post(`/marketplace/catalog/products/${id}/purchase-intent`, {});
  },
  catalogCourses(params = {}) {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null)).toString();
    return api.get(`/marketplace/catalog/courses${q ? `?${q}` : ''}`);
  },
  catalogCourse(id) { return api.get(`/marketplace/catalog/courses/${id}`); },
  // M5 — carrito, direcciones y checkout (pago con candado legal)
  getCart() { return api.get('/marketplace/cart'); },
  addCartItem(body) { return api.post('/marketplace/cart/items', body); },
  updateCartItem(id, body) { return api.patch(`/marketplace/cart/items/${id}`, body); },
  removeCartItem(id) { return api.delete(`/marketplace/cart/items/${id}`); },
  clearCart() { return api.delete('/marketplace/cart'); },
  listAddresses() { return api.get('/marketplace/addresses'); },
  createAddress(body) { return api.post('/marketplace/addresses', body); },
  updateAddress(id, body) { return api.patch(`/marketplace/addresses/${id}`, body); },
  deleteAddress(id) { return api.delete(`/marketplace/addresses/${id}`); },
  checkoutSummary(params = {}) {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null)).toString();
    return api.get(`/marketplace/checkout/summary${q ? `?${q}` : ''}`);
  },
  checkoutPay(body) { return api.post('/marketplace/checkout/pay', body); },
  setSellerShipping(body) { return api.patch('/marketplace/seller/shipping', body); },
  // Panel de Marca — resumen de ventas/demanda sobre datos reales
  sellerOverview() { return api.get('/marketplace/seller/overview'); },
  // Fase 1 — métricas por producto y edición rápida inline
  productMetrics() { return api.get('/marketplace/seller/products/metrics'); },
  quickUpdateProduct(id, body) { return api.patch(`/marketplace/seller/products/${id}/quick`, body); },
};

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

// URL del streaming PROTEGIDO (cookie de sesión viaja en cada request).
export const lessonStreamUrl = (courseId, lessonId) =>
  `${BACKEND}/api/marketplace/courses/${courseId}/lessons/${lessonId}/stream`;

// Subida de video de lección con progreso real (XHR) al endpoint protegido.
export const uploadLessonVideo = (courseId, lessonId, file, { onProgress } = {}) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND}/api/marketplace/seller/courses/${courseId}/lessons/${lessonId}/video`);
    xhr.withCredentials = true;
    const token = localStorage.getItem('lineage_token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let payload = null;
      try { payload = JSON.parse(xhr.responseText); } catch { /* noop */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(payload);
      else reject(new Error(payload?.detail || tr("Error {0}", xhr.status)));
    };
    xhr.onerror = () => reject(new Error(tr("Error de red al subir el video")));
    const fd = new FormData();
    fd.append('file', file);
    xhr.send(fd);
  });

export const marketplaceAdminApi = {
  getConfig() { return api.get('/admin/marketplace/config'); },
  patchConfig(patch) { return api.patch('/admin/marketplace/config', patch); },
  listSellers(status) {
    const q = status ? `?status=${status}` : '';
    return api.get(`/admin/marketplace/sellers${q}`);
  },
  reviewSeller(id, action, reason) {
    return api.post(`/admin/marketplace/sellers/${id}/review`, { action, reason });
  },
  // M2 — moderación de productos
  listProducts(status) {
    const q = status ? `?status=${status}` : '';
    return api.get(`/admin/marketplace/products${q}`);
  },
  reviewProduct(id, action, reason) {
    return api.post(`/admin/marketplace/products/${id}/review`, { action, reason });
  },
  // M3 — moderación de cursos
  listCourses(status) {
    const q = status ? `?status=${status}` : '';
    return api.get(`/admin/marketplace/courses${q}`);
  },
  reviewCourse(id, action, reason) {
    return api.post(`/admin/marketplace/courses/${id}/review`, { action, reason });
  },
};

export default marketplaceApi;
